import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InsightsStore from '@/store/InsightsStore';
import StationStore from '@/store/Station/StationStore';
import DevicesStore from '@/store/Devices/DevicesStore';
import { StationInsight } from '@/types/insights.types';
import { convertInsightToStationInsight } from '../utils/insightConverter';

interface UseInsightsProps {
  insightsStore: InsightsStore;
  stationStore: StationStore;
  devicesStore: DevicesStore;
}

export const useInsights = ({ insightsStore, stationStore, devicesStore }: UseInsightsProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (insightsStore && stationStore?.currentStation?.id) {
      insightsStore.getAssistantInsights();
    }
  }, [insightsStore, stationStore?.currentStation?.id]);

  const availableInsights = insightsStore?.availableInsightsList || [];
  const archivedInsights = insightsStore?.archivedInsightsList || [];
  const publishedInsights = insightsStore?.publishedInsightsList || [];
  const draftInsights = insightsStore?.draftInsightsList || [];

  const availableStationInsights: StationInsight[] = useMemo(
    () => availableInsights.map(insight => convertInsightToStationInsight(insight, stationStore, devicesStore, t)),
    [availableInsights, stationStore, devicesStore, t],
  );

  const archivedStationInsights: StationInsight[] = useMemo(
    () => archivedInsights.map(insight => convertInsightToStationInsight(insight, stationStore, devicesStore, t)),
    [archivedInsights, stationStore, devicesStore, t],
  );

  const publishedStationInsights: StationInsight[] = useMemo(
    () => publishedInsights.map(insight => convertInsightToStationInsight(insight, stationStore, devicesStore, t)),
    [publishedInsights, stationStore, devicesStore, t],
  );

  const draftStationInsights: StationInsight[] = useMemo(
    () => draftInsights.map(insight => convertInsightToStationInsight(insight, stationStore, devicesStore, t)),
    [draftInsights, stationStore, devicesStore, t],
  );

  return {
    availableStationInsights,
    archivedStationInsights,
    publishedStationInsights,
    draftStationInsights,
    isLoading: insightsStore?.isLoadingAvailableInsights || false,
    isUpdating: insightsStore?.isUpdatingStatus || false,
    availableInsights: insightsStore?.availableInsights || [],
    archivedInsights: insightsStore?.archivedInsightsList || [],
    publishedInsights: insightsStore?.publishedInsightsList || [],
    draftInsights: insightsStore?.draftInsightsList || [],
  };
};
